import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ItemGroup, ItemGroupFormData, itemTypes } from '@/types/itemGroup';

// Mock data - replace with API calls
const mockItemGroup: ItemGroup = {
  id: '1',
  code: 'MED',
  name: 'Medicines',
  itemType: 'Inventory',
  isActive: true,
  createdAt: '2023-01-15T10:30:00Z',
  updatedAt: '2023-01-15T10:30:00Z',
};

const ItemGroupForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const isViewMode = location.pathname.includes('/view');
  const [loading, setLoading] = useState(isEditMode || isViewMode);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState<ItemGroupFormData>({
    code: '',
    name: '',
    itemType: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof ItemGroupFormData, string>>>({});

  // Load item group data in edit/view mode
  useEffect(() => {
    if (isEditMode || isViewMode) {
      const fetchItemGroup = async () => {
        setLoading(true);
        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // In a real app, you would fetch the item group by ID
          const fetchedItemGroup = mockItemGroup; // Replace with API call
          
          setFormData({
            code: fetchedItemGroup.code,
            name: fetchedItemGroup.name,
            itemType: fetchedItemGroup.itemType,
            isActive: fetchedItemGroup.isActive,
          });
          
        } catch (error) {
          console.error('Error fetching item group:', error);
          // Handle error (e.g., show error message, redirect, etc.)
        } finally {
          setLoading(false);
        }
      };

      fetchItemGroup();
    }
  }, [id, isEditMode, isViewMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof ItemGroupFormData;
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error when field is edited
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    const fieldName = name as keyof ItemGroupFormData;
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error when field is edited
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ItemGroupFormData, string>> = {};
    
    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.itemType) {
      newErrors.itemType = 'Item type is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real app, you would call your API here
      console.log('Form submitted:', formData);
      
      // Show success message and redirect
      alert(`Item group ${isEditMode ? 'updated' : 'created'} successfully!`);
      navigate('/settings/item-groups');
      
    } catch (error) {
      console.error('Error saving item group:', error);
      alert('An error occurred while saving the item group. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
        <Typography variant="h5" gutterBottom>
          {isViewMode ? 'View Item Group' : isEditMode ? 'Edit Item Group' : 'Add New Item Group'}
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Code"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                required
                error={Boolean(errors.code)}
                helperText={errors.code}
                margin="normal"
                disabled={isViewMode}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                error={Boolean(errors.name)}
                helperText={errors.name}
                margin="normal"
                disabled={isViewMode}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                margin="normal" 
                required 
                error={Boolean(errors.itemType)}
                disabled={isViewMode}
              >
                <InputLabel>Item Type</InputLabel>
                <Select
                  name="itemType"
                  value={formData.itemType}
                  onChange={handleSelectChange}
                  label="Item Type"
                >
                  <MenuItem value="">
                    <em>Select Item Type</em>
                  </MenuItem>
                  {itemTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {errors.itemType && (
                  <FormHelperText>{errors.itemType}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                margin="normal" 
                required
                error={Boolean(errors.isActive)}
                disabled={isViewMode}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  name="isActive"
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => {
                    const isActive = e.target.value === 'active';
                    setFormData(prev => ({
                      ...prev,
                      isActive
                    }));
                  }}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
                {errors.isActive && (
                  <FormHelperText>{errors.isActive}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {!isViewMode && (
              <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/settings/item-groups')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : null}
                >
                  {submitting ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
                </Button>
              </Grid>
            )}
            
            {isViewMode && (
              <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/settings/item-groups')}
                >
                  Back to List
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(`/settings/item-groups/${id}/edit`)}
                >
                  Edit
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default ItemGroupForm;
